/**
 * Quote Detail Content
 *
 * Panel content showing quote details with accept/reject/counter-offer functionality.
 * Rendered inside parent's SplitPanel.
 * Issue #809 - Quote Lifecycle enhancements (counter-offer flow)
 * Migrated from QuoteDetailDrawer to SplitPanel pattern per CLAUDE.md rule #14.
 */

import AcceptIcon from '@mui/icons-material/Check';
import RejectIcon from '@mui/icons-material/Close';
import CounterOfferIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  iconSizes,
  MagicInput,
  MenuItem,
  ShimmerLoader,
  SplitPanelContent,
  SplitPanelFooter,
  SplitPanelHeader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useState } from 'react';
import { useAcceptQuote, useConsumerQuoteDetail, useCounterOfferQuote, useRejectQuote } from '../../lib/trpc/hooks';

export interface QuoteDetailContentProps {
  quoteId: string | null;
  onClose: () => void;
}

const RESPONDABLE_QUOTE_STATUSES: ReadonlySet<string> = new Set(['pending', 'active', 'sent', 'viewed']);

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  draft: 'default',
  active: 'info',
  pending: 'info',
  sent: 'primary',
  viewed: 'primary',
  locked: 'warning',
  accepted: 'success',
  rejected: 'error',
  declined: 'error',
  expired: 'warning',
  counter_offer: 'info',
};

/** Standard term options in months for counter-offer select */
const TERM_OPTIONS = [6, 12, 24, 36, 48, 60, 84, 120, 180, 240, 360];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function QuoteDetailContent({ quoteId, onClose }: QuoteDetailContentProps) {
  const { t } = useI18n();
  const [confirmAction, setConfirmAction] = useState<'accept' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showCounterOffer, setShowCounterOffer] = useState(false);

  // Counter-offer form state
  const [proposedRate, setProposedRate] = useState('');
  const [proposedTerm, setProposedTerm] = useState('');
  const [proposedAmount, setProposedAmount] = useState('');
  const [counterOfferReason, setCounterOfferReason] = useState('');
  const [counterOfferValidationError, setCounterOfferValidationError] = useState('');

  // Fetch quote detail
  const { data: quote, isLoading, isError, error } = useConsumerQuoteDetail(quoteId);

  // Mutations
  const acceptMutation = useAcceptQuote();
  const rejectMutation = useRejectQuote();
  const counterOfferMutation = useCounterOfferQuote();
  const isMutating = acceptMutation.isPending || rejectMutation.isPending || counterOfferMutation.isPending;

  const handleAccept = () => {
    setConfirmAction('accept');
  };

  const handleReject = () => {
    setConfirmAction('reject');
  };

  const handleToggleCounterOffer = () => {
    setShowCounterOffer((prev) => !prev);
    setCounterOfferValidationError('');
  };

  const resetCounterOfferForm = () => {
    setProposedRate('');
    setProposedTerm('');
    setProposedAmount('');
    setCounterOfferReason('');
    setCounterOfferValidationError('');
    setShowCounterOffer(false);
  };

  const handleSubmitCounterOffer = async () => {
    if (!quoteId) return;

    const rate = proposedRate ? Number.parseFloat(proposedRate) : undefined;
    const term = proposedTerm ? Number.parseInt(proposedTerm, 10) : undefined;
    const amount = proposedAmount ? Number.parseFloat(proposedAmount) : undefined;

    // Validate at least one field is proposed
    if (!rate && !term && !amount) {
      setCounterOfferValidationError(t('quotes.counterOffer.atLeastOneField'));
      return;
    }

    // Validate reason is provided
    if (!counterOfferReason.trim()) {
      setCounterOfferValidationError(t('quotes.counterOffer.reason'));
      return;
    }

    setCounterOfferValidationError('');

    try {
      await counterOfferMutation.mutateAsync({
        quoteId,
        proposedRate: rate,
        proposedTerm: term,
        proposedAmount: amount,
        reason: counterOfferReason.trim(),
      });
      resetCounterOfferForm();
    } catch (_err) {
      // Error is available via mutation state
    }
  };

  const handleConfirmResponse = async () => {
    if (!quoteId || !confirmAction) return;

    try {
      if (confirmAction === 'accept') {
        await acceptMutation.mutateAsync({ quoteId });
      } else {
        await rejectMutation.mutateAsync({
          quoteId,
          reason: rejectReason || undefined,
        });
      }
      setConfirmAction(null);
      setRejectReason('');
    } catch (_err) {
      // Error is available via mutation state
    }
  };

  const handleCloseDialog = () => {
    setConfirmAction(null);
    setRejectReason('');
  };

  const getStatusLabel = (status: string): string => {
    const key = `quotes.status.${status}` as const;
    const translated = t(key);
    return translated !== key ? translated : status.charAt(0).toUpperCase() + status.slice(1);
  };

  const canRespond = quote ? RESPONDABLE_QUOTE_STATUSES.has(quote.status) : false;

  // Extract fee data from calculations if available
  const calculations = (quote?.calculations ?? {}) as Record<string, unknown>;
  const originationFee = typeof calculations.origination_fee === 'number' ? calculations.origination_fee : null;
  const appraisalFee = typeof calculations.appraisal_fee === 'number' ? calculations.appraisal_fee : null;
  const totalClosingCosts =
    typeof calculations.total_closing_costs === 'number' ? calculations.total_closing_costs : null;
  const conditions = typeof calculations.conditions === 'string' ? calculations.conditions : null;
  const isLocked = quote?.status === 'locked';

  return (
    <>
      <SplitPanelHeader
        title={quote ? `${t('quotes.detail')} #${quote.id.slice(0, 8)}` : t('quotes.detail')}
        subtitle={
          quote ? (
            <GlassChip
              label={getStatusLabel(quote.status)}
              size="small"
              color={STATUS_COLORS[quote.status] || 'default'}
              sx={{ ml: 1 }}
            />
          ) : undefined
        }
        onClose={onClose}
      />

      <SplitPanelContent>
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <ShimmerLoader variant="rectangular" height={200} sx={{ mb: 2 }} />
            <ShimmerLoader variant="rectangular" height={150} sx={{ mb: 2 }} />
            <ShimmerLoader variant="rectangular" height={100} />
          </Box>
        ) : isError || !quote ? (
          <Box sx={{ p: 2 }}>
            <GlassAlert severity="error" visible={true}>
              {error?.message || t('error.unknown')}
            </GlassAlert>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Mutation errors */}
            {(acceptMutation.isError || rejectMutation.isError) && (
              <GlassAlert severity="error" visible={true}>
                {t('quotes.responseError')}
              </GlassAlert>
            )}
            {counterOfferMutation.isError && (
              <GlassAlert severity="error" visible={true}>
                {t('quotes.counterOffer.error')}
              </GlassAlert>
            )}
            {counterOfferMutation.isSuccess && (
              <GlassAlert severity="success" visible={true}>
                {t('quotes.counterOffer.success')}
              </GlassAlert>
            )}

            {/* Section 1: Loan Summary */}
            <GlassCard neonAccent="cyan">
              <Typography variant="h6" gutterBottom>
                {t('quotes.loanSummary')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <DetailRow label={t('quotes.loanAmount')} value={formatCurrency(quote.amount)} bold />
                <DetailRow label={t('quotes.interestRate')} value={`${quote.rate}%`} />
                <DetailRow
                  label={t('quotes.term')}
                  value={
                    quote.term >= 12
                      ? t('quotes.termYears', { count: Math.round(quote.term / 12) })
                      : t('quotes.termMonths', { count: quote.term })
                  }
                />
                <DetailRow
                  label={t('quotes.monthlyPayment')}
                  value={formatCurrency(quote.monthlyPayment)}
                  bold
                  highlight
                />
              </Box>
            </GlassCard>

            {/* Section 2: Fee Breakdown */}
            <GlassCard neonAccent="amber">
              <Typography variant="h6" gutterBottom>
                {t('quotes.feeBreakdown')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <DetailRow
                  label={t('quotes.originationFee')}
                  value={originationFee !== null ? formatCurrency(originationFee) : '-'}
                />
                <DetailRow
                  label={t('quotes.appraisalFee')}
                  value={appraisalFee !== null ? formatCurrency(appraisalFee) : '-'}
                />
                <Divider />
                <DetailRow
                  label={t('quotes.totalClosingCosts')}
                  value={totalClosingCosts !== null ? formatCurrency(totalClosingCosts) : '-'}
                  bold
                />
              </Box>
            </GlassCard>

            {/* Section 3: Terms & Conditions */}
            <GlassCard neonAccent="green">
              <Typography variant="h6" gutterBottom>
                {t('quotes.terms')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <DetailRow label={t('quotes.expirationDate')} value={formatDate(quote.expiresAt)} />
                <DetailRow
                  label={t('quotes.lockStatus')}
                  value={isLocked ? t('quotes.locked') : t('quotes.unlocked')}
                />
                <DetailRow label={t('quotes.conditions')} value={conditions || t('quotes.noConditions')} />
                <DetailRow label={t('quotes.receivedDate')} value={formatDate(quote.receivedAt)} />
                {quote.caseId && <DetailRow label={t('quotes.linkedCase')} value={quote.caseId.slice(0, 8)} />}
              </Box>
            </GlassCard>

            {/* Counter-Offer Form (collapsible) */}
            {canRespond && (
              <Collapse in={showCounterOffer}>
                <GlassCard neonAccent="amber">
                  <Typography variant="h6" gutterBottom>
                    {t('quotes.counterOffer.title')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {t('quotes.counterOffer.description')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <MagicInput
                      label={t('quotes.counterOffer.proposedRate')}
                      type="number"
                      value={proposedRate}
                      onChange={(e) => setProposedRate(e.target.value)}
                      placeholder={`${t('quotes.interestRate')}: ${quote.rate}%`}
                      size="small"
                      slotProps={{ input: { inputProps: { min: 0, max: 100, step: 0.125 } } }}
                    />
                    <MagicInput
                      label={t('quotes.counterOffer.proposedTerm')}
                      select
                      value={proposedTerm}
                      onChange={(e) => setProposedTerm(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="">
                        <em>
                          {t('quotes.term')}:{' '}
                          {quote.term >= 12
                            ? t('quotes.termYears', { count: Math.round(quote.term / 12) })
                            : t('quotes.termMonths', { count: quote.term })}
                        </em>
                      </MenuItem>
                      {TERM_OPTIONS.map((months) => (
                        <MenuItem key={months} value={months}>
                          {months >= 12
                            ? t('quotes.termYears', { count: Math.round(months / 12) })
                            : t('quotes.termMonths', { count: months })}
                        </MenuItem>
                      ))}
                    </MagicInput>
                    <MagicInput
                      label={t('quotes.counterOffer.proposedAmount')}
                      type="number"
                      value={proposedAmount}
                      onChange={(e) => setProposedAmount(e.target.value)}
                      placeholder={`${t('quotes.loanAmount')}: ${formatCurrency(quote.amount)}`}
                      size="small"
                      slotProps={{ input: { inputProps: { min: 0, step: 1000 } } }}
                    />
                    <MagicInput
                      label={t('quotes.counterOffer.reason')}
                      placeholder={t('quotes.counterOffer.reasonPlaceholder')}
                      multiline
                      rows={3}
                      value={counterOfferReason}
                      onChange={(e) => setCounterOfferReason(e.target.value)}
                      size="small"
                    />
                    {counterOfferValidationError && (
                      <GlassAlert severity="warning" visible={true}>
                        {counterOfferValidationError}
                      </GlassAlert>
                    )}
                    <GlassButton
                      variant="glow"
                      color="primary"
                      startIcon={<CounterOfferIcon />}
                      onClick={handleSubmitCounterOffer}
                      disabled={isMutating}
                      fullWidth
                    >
                      {isMutating ? t('quotes.submitting') : t('quotes.counterOffer.submit')}
                    </GlassButton>
                  </Box>
                </GlassCard>
              </Collapse>
            )}

            {/* Response Status (if already responded) */}
            {(quote.status === 'accepted' || quote.status === 'rejected' || quote.status === 'counter_offer') && (
              <GlassCard
                neonAccent={quote.status === 'accepted' ? 'green' : quote.status === 'counter_offer' ? 'amber' : 'cyan'}
              >
                <Typography variant="h6" gutterBottom>
                  {t('quotes.yourResponse')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  {quote.status === 'accepted' ? (
                    <>
                      <AcceptIcon sx={{ fontSize: iconSizes['3xl'], color: 'success.main', mb: 1 }} />
                      <Typography sx={{ color: 'success.main', fontWeight: 600 }}>{t('quotes.youAccepted')}</Typography>
                    </>
                  ) : quote.status === 'counter_offer' ? (
                    <>
                      <CounterOfferIcon sx={{ fontSize: iconSizes['3xl'], color: 'info.main', mb: 1 }} />
                      <Typography sx={{ color: 'info.main', fontWeight: 600 }}>
                        {t('quotes.counterOffer.sent')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <RejectIcon sx={{ fontSize: iconSizes['3xl'], color: 'error.main', mb: 1 }} />
                      <Typography sx={{ color: 'error.main', fontWeight: 600 }}>{t('quotes.youRejected')}</Typography>
                    </>
                  )}
                </Box>
              </GlassCard>
            )}
          </Box>
        )}
      </SplitPanelContent>

      {/* Footer with action buttons */}
      {canRespond && (
        <SplitPanelFooter>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%' }}>
            <GlassButton
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={handleReject}
              disabled={isMutating}
            >
              {t('quotes.reject')}
            </GlassButton>
            <GlassButton
              variant="outlined"
              color="primary"
              startIcon={<CounterOfferIcon />}
              onClick={handleToggleCounterOffer}
              disabled={isMutating}
            >
              {t('quotes.counter')}
            </GlassButton>
            <GlassButton
              variant="glow"
              color="success"
              startIcon={<AcceptIcon />}
              onClick={handleAccept}
              disabled={isMutating}
            >
              {t('quotes.accept')}
            </GlassButton>
          </Box>
        </SplitPanelFooter>
      )}

      {/* Accept Confirmation Dialog */}
      <Dialog open={confirmAction === 'accept'} onClose={handleCloseDialog}>
        <DialogTitle>{t('quotes.confirmAccept')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('quotes.confirmAcceptMessage')}</DialogContentText>
          {quote && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('quotes.loanSummary')}
              </Typography>
              <Typography variant="body2">
                {formatCurrency(quote.amount)} at {quote.rate}% for{' '}
                {quote.term >= 12
                  ? t('quotes.termYears', { count: Math.round(quote.term / 12) })
                  : t('quotes.termMonths', { count: quote.term })}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('quotes.monthlyPayment')}: {formatCurrency(quote.monthlyPayment)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleCloseDialog} disabled={isMutating}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton onClick={handleConfirmResponse} color="success" variant="glow" disabled={isMutating}>
            {isMutating ? t('quotes.submitting') : t('quotes.accept')}
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={confirmAction === 'reject'} onClose={handleCloseDialog}>
        <DialogTitle>{t('quotes.confirmReject')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('quotes.confirmRejectMessage')}</DialogContentText>
          <MagicInput
            autoFocus
            margin="dense"
            label={t('quotes.rejectReason')}
            placeholder={t('quotes.rejectReasonPlaceholder')}
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleCloseDialog} disabled={isMutating}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton onClick={handleConfirmResponse} color="error" variant="glow" disabled={isMutating}>
            {isMutating ? t('quotes.submitting') : t('quotes.reject')}
          </GlassButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Detail row helper component for consistent key-value display
 */
function DetailRow({
  label,
  value,
  bold,
  highlight,
}: {
  label: string;
  value: string;
  bold?: boolean;
  highlight?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: bold ? 600 : 400, color: highlight ? 'primary.main' : 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  );
}
